import React from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Box, Divider, Switch, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, Avatar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Dashboard as DashboardIcon,
  SwapHoriz as SwapHorizIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AdminPanelSettings as AdminIcon,
  AttachMoney as AttachMoneyIcon,
  Lightbulb as LightbulbIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const ORANGE       = '#F97316';
const ORANGE_DARK  = '#EA580C';
const ORANGE_SOFT  = 'rgba(249,115,22,0.55)';
const ORANGE_TINT  = 'rgba(249,115,22,0.09)';
const ORANGE_HOVER = 'rgba(249,115,22,0.07)';
const ORANGE_LINE  = 'rgba(249,115,22,0.18)';

/* Barra lateral do item ativo */
const ActiveBar = () => (
  <Box sx={{
    position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
    width: 3, height: '55%', bgcolor: ORANGE, borderRadius: '0 3px 3px 0',
  }} />
);

/* Item de menu reutilizável */
const NavItem = ({ item, isActive, onClick }) => (
  <ListItem disablePadding sx={{ position: 'relative' }}>
    {isActive && <ActiveBar />}
    <ListItemButton
      onClick={onClick}
      sx={{
        px: 2.5, py: 1, pl: isActive ? 3 : 2.5,
        mr: 1, borderRadius: '0 8px 8px 0',
        backgroundColor: isActive ? ORANGE_TINT : 'transparent',
        color: isActive ? ORANGE_DARK : 'text.primary',
        transition: 'all 0.15s ease',
        '&:hover': { backgroundColor: ORANGE_HOVER, color: ORANGE_DARK },
      }}
    >
      <ListItemIcon sx={{
        minWidth: 34,
        color: isActive ? ORANGE : ORANGE_SOFT,
        transition: 'color 0.15s',
      }}>
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.text}
        primaryTypographyProps={{ fontSize: '0.875em', fontWeight: isActive ? 600 : 400 }}
      />
    </ListItemButton>
  </ListItem>
);

/* Título de seção */
const SectionTitle = ({ children }) => (
  <Typography sx={{
    fontSize: '0.68em', fontWeight: 700, color: ORANGE,
    letterSpacing: '1.3px', textTransform: 'uppercase',
  }}>
    {children}
  </Typography>
);

const Sidebar = ({ open, onClose, onSettingsOpen }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user }  = useAuth();

  const avatarLetter = user?.nome ? user.nome.charAt(0).toUpperCase() : 'U';
  const isSecretaria = user?.role === 'secretaria';
  // Só o dentista dono da clínica vê tudo sem filtro; secretaria e dentista
  // parceiro são sempre limitados pelas permissoes configuradas pro usuário.
  const temPermissoesLimitadas = user?.role !== 'dentista';
  const perm = user?.permissoes || {};
  const tipoLabel = isSecretaria ? 'Secretaria'
    : user?.tipoPessoa === 'PJ' ? 'PJ — Simples Nacional'
    : user?.tipoPessoa === 'PF' ? 'PF — Carnê-Leão'
    : user?.tipoPessoa === 'HIBRIDO' ? 'PF + PJ — Híbrido'
    : 'Dentista';

  const visaoGeralItemsTodos = [
    { text: 'Dashboard',      icon: <DashboardIcon   fontSize="small" />, path: '/dashboard',    modulo: 'dashboard' },
    { text: 'Faturamento',    icon: <TrendingUpIcon  fontSize="small" />, path: '/faturamento',  modulo: 'faturamento' },
    { text: 'Despesas',       icon: <ReceiptIcon     fontSize="small" />, path: '/despesas',     modulo: 'despesas' },
    { text: 'Plano de Contas',icon: <DescriptionIcon fontSize="small" />, path: '/plano-contas', modulo: 'planoContas' },
  ];

  const gestaoClinicaItemsTodos = [
    { text: 'Dashboard Clínica', icon: <AdminIcon       fontSize="small" />, path: '/clinica-dashboard', modulo: 'clinicaDashboard' },
    { text: 'Pacientes',         icon: <ReceiptIcon     fontSize="small" />, path: '/pacientes',         modulo: 'pacientes' },
    { text: 'Procedimentos',     icon: <DescriptionIcon fontSize="small" />, path: '/procedimentos',     modulo: 'procedimentos' },
    { text: 'Agenda',            icon: <ReceiptIcon     fontSize="small" />, path: '/agenda',            modulo: 'agenda' },
    { text: 'Termos & Docs',     icon: <DescriptionIcon fontSize="small" />, path: '/termos',            modulo: 'pacientes' },
  ];

  // Filtra itens conforme permissões (só o dono da clínica vê tudo sem filtro)
  const visaoGeralItems = temPermissoesLimitadas
    ? visaoGeralItemsTodos.filter(i => perm[i.modulo])
    : visaoGeralItemsTodos;

  const gestaoClinicaItems = temPermissoesLimitadas
    ? gestaoClinicaItemsTodos.filter(i => perm[i.modulo])
    : gestaoClinicaItemsTodos;

  const adminMenuItems = user?.isAdmin ? [{
    section: 'Administração',
    items: [
      { text: 'Dashboard Admin',   icon: <AdminIcon    fontSize="small" />, path: '/admin' },
      { text: 'Painel Operacional',icon: <SwapHorizIcon fontSize="small" />, path: '/admin/operacional' },
    ],
  }] : [];

  const assinaturaItem  = { text: 'Assinatura',   icon: <AttachMoneyIcon fontSize="small" />, path: '/usuario/assinatura' };
  const configuracaoItem= { text: 'Configuração',  icon: <SettingsIcon   fontSize="small" />, path: '/usuario/configuracao' };
  const sugestoesItem   = { text: 'Sugestões',     icon: <LightbulbIcon  fontSize="small" />, path: '/sugestoes' };
  const usuariosItem    = { text: 'Usuários',       icon: <PeopleIcon     fontSize="small" />, path: '/gerenciar-usuarios' };

  const go = (path) => { navigate(path); if (onClose) onClose(); };
  const handleItem = (item) => {
    if (item.action === 'openSettings' && onSettingsOpen) { onSettingsOpen(); if (onClose) onClose(); }
    else if (item.path) go(item.path);
  };

  const accordionSx = {
    boxShadow: 'none', background: 'transparent', '&:before': { display: 'none' },
  };
  const summarySx = {
    minHeight: '38px', px: 2.5,
    '& .MuiAccordionSummary-content': { my: 0.5 },
    '&:hover': { background: ORANGE_HOVER },
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 300,
          boxSizing: 'border-box',
          mt: '50px',
          height: 'calc(100vh - 50px)',
          display: 'flex',
          flexDirection: 'column',
          background: isDarkMode ? '#1C1917' : '#FFFAF5',
          borderRight: `1px solid ${ORANGE_LINE}`,
        },
      }}
    >
      <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* ── Mini card de perfil ── */}
        <Box sx={{
          mx: 1.5, mt: 2, mb: 1, p: 1.5,
          background: isDarkMode ? 'rgba(249,115,22,0.1)' : ORANGE_TINT,
          borderRadius: 2,
          border: `1px solid ${ORANGE_LINE}`,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Avatar sx={{
            width: 38, height: 38, bgcolor: ORANGE,
            fontSize: '1rem', fontWeight: 700, flexShrink: 0,
            boxShadow: '0 2px 8px rgba(249,115,22,0.35)',
          }}>
            {avatarLetter}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} fontSize="0.85em" noWrap>
              {user?.nome || 'Usuário'}
            </Typography>
            <Typography variant="caption" sx={{ color: ORANGE, fontSize: '0.7em', fontWeight: 600 }}>
              {tipoLabel}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mx: 1.5, mb: 0.5, borderColor: ORANGE_LINE }} />

        {/* ── Visão Geral ── */}
        <Accordion defaultExpanded={false} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ORANGE, fontSize: '1.1em' }} />} sx={summarySx}>
            <SectionTitle>Visão Geral</SectionTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, py: 0.5 }}>
            <List sx={{ py: 0 }}>
              {visaoGeralItems.map((item, i) => (
                <NavItem key={i} item={item} isActive={location.pathname === item.path} onClick={() => handleItem(item)} />
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ mx: 1.5, borderColor: ORANGE_LINE }} />

        {/* ── Gestão da Clínica ── */}
        <Accordion defaultExpanded={false} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ORANGE, fontSize: '1.1em' }} />} sx={summarySx}>
            <SectionTitle>Gestão da Clínica</SectionTitle>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, py: 0.5 }}>
            <List sx={{ py: 0 }}>
              {gestaoClinicaItems.map((item, i) => (
                <NavItem key={i} item={item} isActive={location.pathname === item.path} onClick={() => handleItem(item)} />
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ mx: 1.5, borderColor: ORANGE_LINE }} />

        {/* ── Admin (se aplicável) ── */}
        {adminMenuItems.map((section, si) => (
          <Box key={si}>
            <Typography sx={{
              fontSize: '0.68em', fontWeight: 700, color: ORANGE,
              letterSpacing: '1.3px', textTransform: 'uppercase',
              px: 2.5, pt: 1.5, pb: 0.5, display: 'block',
            }}>
              {section.section}
            </Typography>
            <List sx={{ py: 0 }}>
              {section.items.map((item, ii) => (
                <NavItem key={ii} item={item} isActive={location.pathname === item.path} onClick={() => handleItem(item)} />
              ))}
            </List>
            <Divider sx={{ mx: 1.5, borderColor: ORANGE_LINE }} />
          </Box>
        ))}

        {/* ── Assinatura e Configuração ── */}
        <Box sx={{ mx: 1.5, mt: 1, mb: 0.5, borderRadius: 2, border: `1px solid ${ORANGE_LINE}`, overflow: 'hidden' }}>
          {!isSecretaria && (
            <>
              <NavItem item={sugestoesItem} isActive={location.pathname === sugestoesItem.path} onClick={() => handleItem(sugestoesItem)} />
              <Divider sx={{ borderColor: ORANGE_LINE }} />
              <NavItem item={assinaturaItem} isActive={location.pathname === assinaturaItem.path} onClick={() => handleItem(assinaturaItem)} />
              <Divider sx={{ borderColor: ORANGE_LINE }} />
            </>
          )}
          <NavItem item={configuracaoItem} isActive={location.pathname === configuracaoItem.path} onClick={() => handleItem(configuracaoItem)} />
        </Box>

        {/* ── Tema ── */}
        <Divider sx={{ mt: 'auto', borderColor: ORANGE_LINE }} />
        <Box sx={{
          mx: 1.5, my: 1.5, px: 2, py: 1.5,
          background: isDarkMode ? 'rgba(249,115,22,0.1)' : ORANGE_TINT,
          borderRadius: 2, border: `1px solid ${ORANGE_LINE}`,
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={toggleTheme}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                {isDarkMode
                  ? <DarkModeIcon  fontSize="small" sx={{ color: ORANGE }} />
                  : <LightModeIcon fontSize="small" sx={{ color: ORANGE }} />
                }
                <Typography variant="body2" fontWeight={500} fontSize="0.85em">
                  {isDarkMode ? 'Tema Escuro' : 'Tema Claro'}
                </Typography>
              </Box>
            }
          />
        </Box>

        {/* ── Versão ── */}
        <Box sx={{
          px: 2.5, py: 1.25,
          borderTop: `1px solid ${ORANGE_LINE}`,
          background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(249,115,22,0.03)',
        }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72em' }}>
            Versão 1.0.0
          </Typography>
        </Box>

      </Box>
    </Drawer>
  );
};

export default Sidebar;
